import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { fetchUsers, createUser, updateUser, deleteUser } from '../../api/admin';
import { fetchAllStudents } from '../../api/students';
import UserFormDialog from '../../components/forms/UserFormDialog';
import ConfirmDialog from '../../components/feedback/ConfirmDialog';
import { ROLES } from '../../utils/roles';

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 2px 12px rgba(67,87,173,0.08)', bgcolor: '#fff' };
const avatarColors = ['#EC4899', '#8B5CF6', '#06B6D4', '#22C55E', '#F59E0B', '#4357AD', '#EF4444', '#14B8A6'];

const ParentsPage = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedParent, setSelectedParent] = useState(null);

  const openDialog = (parent) => {
    setDialogOpen(false);
    setSelectedParent(parent || null);
    setTimeout(() => setDialogOpen(true), 0);
  };
  const [confirmState, setConfirmState] = useState(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const queryClient = useQueryClient();

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const studentsQuery = useQuery({ queryKey: ['students'], queryFn: fetchAllStudents });

  const allParents = useMemo(
    () => (usersQuery.data || []).filter((user) => user.role === ROLES.PARENT),
    [usersQuery.data]
  );

  const parentChildren = useMemo(() => {
    const map = {};
    (studentsQuery.data || []).forEach((student) => {
      if (student.parentId) {
        if (!map[student.parentId]) map[student.parentId] = [];
        map[student.parentId].push(student);
      }
    });
    return map;
  }, [studentsQuery.data]);

  const parents = useMemo(() => {
    let list = [...allParents];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((p) => p.name?.toLowerCase().includes(q) || p.email?.toLowerCase().includes(q));
    }
    if (sortBy === 'name') {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'uk'));
    } else if (sortBy === 'children') {
      list.sort((a, b) => (parentChildren[b.id]?.length || 0) - (parentChildren[a.id]?.length || 0));
    }
    return list;
  }, [allParents, search, sortBy, parentChildren]);

  const createMutation = useMutation({
    mutationFn: (payload) => createUser({ ...payload, role: ROLES.PARENT }),
    onSuccess: () => { enqueueSnackbar('Батьківський акаунт створено', { variant: 'success' }); queryClient.invalidateQueries({ queryKey: ['users'] }); },
    onError: () => enqueueSnackbar('Помилка створення акаунта', { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateUser(id, payload),
    onSuccess: () => { enqueueSnackbar('Дані оновлено', { variant: 'success' }); queryClient.invalidateQueries({ queryKey: ['users'] }); },
    onError: () => enqueueSnackbar('Не вдалося оновити', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteUser(id),
    onSuccess: () => { enqueueSnackbar('Акаунт видалено', { variant: 'success' }); queryClient.invalidateQueries({ queryKey: ['users'] }); },
    onError: () => enqueueSnackbar('Не вдалося видалити', { variant: 'error' }),
  });

  const isLoading = usersQuery.isLoading || studentsQuery.isLoading;

  const handleSubmit = async (values) => {
    if (selectedParent) {
      await updateMutation.mutateAsync({ id: selectedParent.id, payload: values });
    } else {
      await createMutation.mutateAsync(values);
    }
  };

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <Card sx={{ ...cardSx, background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)` }}>
        <CardContent sx={{ py: 3, px: 3.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1.5}>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#fff' }}>Батьки</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
                {allParents.length} {allParents.length === 1 ? 'акаунт' : 'акаунтів'}
              </Typography>
            </Box>
            <Button
              variant="contained" size="small"
              startIcon={<AddIcon />}
              onClick={() => openDialog(null)}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, borderRadius: 3, textTransform: 'none', fontWeight: 700, px: 2.5, alignSelf: { xs: 'flex-start', sm: 'auto' } }}
            >
              Додати батьків
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Search + Sort */}
      {allParents.length > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            size="small"
            placeholder="Пошук за іменем..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#8891A5' }} /></InputAdornment>,
            }}
            sx={{
              flex: 1, maxWidth: 320,
              '& .MuiOutlinedInput-root': { borderRadius: 3, '& fieldset': { borderColor: '#EDE9FE' }, '&.Mui-focused fieldset': { borderColor: B.primary } },
            }}
          />
          <Stack direction="row" alignItems="center" spacing={1}>
            <SortIcon sx={{ fontSize: 18, color: '#8891A5' }} />
            <ToggleButtonGroup
              value={sortBy}
              exclusive
              onChange={(_, val) => { if (val) setSortBy(val); }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  border: '1px solid #EDE9FE', borderRadius: '20px !important', px: 2, py: 0.5, textTransform: 'none', fontSize: 12, fontWeight: 600, color: '#8891A5',
                  '&.Mui-selected': { bgcolor: B.primary, color: '#fff', borderColor: B.primary, '&:hover': { bgcolor: '#3648A0' } },
                },
              }}
            >
              <ToggleButton value="name">За алфавітом</ToggleButton>
              <ToggleButton value="children">За к-стю учнів</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
        </Stack>
      )}

      {/* Content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: B.primary }} />
        </Box>
      ) : allParents.length === 0 ? (
        <Card sx={cardSx}>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <FamilyRestroomIcon sx={{ fontSize: 48, color: '#C4B5FD', mb: 1 }} />
            <Typography variant="h6" sx={{ color: B.dark }}>Батьків поки немає</Typography>
            <Typography variant="body2" sx={{ color: '#8891A5' }}>Додайте перший акаунт</Typography>
          </CardContent>
        </Card>
      ) : parents.length === 0 ? (
        <Card sx={cardSx}>
          <CardContent sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#8891A5' }}>Нічого не знайдено за "{search}"</Typography>
          </CardContent>
        </Card>
      ) : (
        <Card sx={cardSx}>
          <Stack divider={<Box sx={{ borderBottom: '1px solid #F1F5F9' }} />}>
            {parents.map((parent, i) => {
              const color = avatarColors[i % avatarColors.length];
              const children = parentChildren[parent.id] || [];
              return (
                <Box
                  key={parent.id}
                  sx={{
                    px: 2.5, py: 1.5,
                    display: 'flex', alignItems: 'center', gap: 2,
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: '#FAFAFE' },
                  }}
                >
                  {/* Avatar */}
                  <Avatar sx={{ width: 38, height: 38, bgcolor: `${color}15`, color, fontWeight: 700, fontSize: 15, border: `2px solid ${color}30`, flexShrink: 0 }}>
                    {parent.name?.charAt(0)?.toUpperCase()}
                  </Avatar>

                  {/* Name + email */}
                  <Box sx={{ minWidth: 140, flex: '0 0 180px' }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: B.dark }} noWrap>{parent.name}</Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <EmailOutlinedIcon sx={{ fontSize: 12, color: '#B0B8C9' }} />
                      <Typography variant="caption" sx={{ color: '#8891A5' }} noWrap>{parent.email}</Typography>
                    </Stack>
                  </Box>

                  {/* Phone + notes */}
                  <Box sx={{ flex: '0 0 150px', display: { xs: 'none', md: 'block' } }}>
                    {parent.phone && (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <PhoneIcon sx={{ fontSize: 13, color: '#22C55E' }} />
                        <Typography variant="caption" fontWeight={600} sx={{ color: B.dark }}>{parent.phone}</Typography>
                      </Stack>
                    )}
                    {parent.notes && (
                      <Tooltip title={parent.notes} arrow>
                        <Typography variant="caption" sx={{ color: '#8891A5', display: 'block', maxWidth: 140 }} noWrap>
                          {parent.notes}
                        </Typography>
                      </Tooltip>
                    )}
                    {!parent.phone && !parent.notes && (
                      <Typography variant="caption" sx={{ color: '#C4B5FD' }}>—</Typography>
                    )}
                  </Box>

                  {/* Children chips */}
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {children.map((child) => (
                      <Chip
                        key={child.id}
                        icon={<ChildCareIcon sx={{ fontSize: 13 }} />}
                        label={child.name}
                        size="small"
                        sx={{ bgcolor: '#EDE9FE', color: B.primary, fontWeight: 600, fontSize: 11, height: 24, '& .MuiChip-icon': { color: B.primary } }}
                      />
                    ))}
                    {children.length === 0 && (
                      <Typography variant="caption" sx={{ color: '#C4B5FD', fontStyle: 'italic' }}>Без учнів</Typography>
                    )}
                  </Box>

                  {/* Last login */}
                  <Box sx={{ flex: '0 0 auto', display: { xs: 'none', md: 'block' }, textAlign: 'right', minWidth: 90 }}>
                    {parent.lastLoginAt ? (
                      <>
                        <Typography variant="caption" fontWeight={600} sx={{ color: B.dark, display: 'block' }}>
                          {dayjs(parent.lastLoginAt).format('DD.MM.YYYY')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#8891A5' }}>
                          {dayjs(parent.lastLoginAt).format('HH:mm')}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" sx={{ color: '#C4B5FD' }}>Не входив</Typography>
                    )}
                  </Box>

                  {/* Password */}
                  {parent.rawPassword && (
                    <Chip label={parent.rawPassword} size="small" sx={{ bgcolor: '#F8F7FF', color: '#8891A5', fontWeight: 500, fontSize: 10, fontFamily: 'monospace', height: 22, display: { xs: 'none', sm: 'flex' } }} />
                  )}

                  {/* Actions */}
                  <Stack direction="row" spacing={0.5} flexShrink={0}>
                    <Tooltip title="Редагувати">
                      <IconButton size="small" onClick={() => openDialog(parent)} sx={{ color: B.primary, '&:hover': { bgcolor: '#F0EDFF' } }}>
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Видалити">
                      <IconButton size="small" onClick={() => setConfirmState(parent)} sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEE2E2' } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Card>
      )}

      <UserFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        initialValues={{
          name: selectedParent?.name || '',
          email: selectedParent?.email || '',
          password: selectedParent?.rawPassword || '',
          phone: selectedParent?.phone || '',
          notes: selectedParent?.notes || '',
        }}
        title={selectedParent ? 'Редагувати батьків' : 'Новий акаунт батьків'}
        isEdit={Boolean(selectedParent)}
        showContactFields
      />

      <ConfirmDialog
        open={Boolean(confirmState)}
        title="Видалити акаунт?"
        description={`Це дію не можна скасувати. Видалити ${confirmState?.name}?`}
        onCancel={() => setConfirmState(null)}
        onConfirm={async () => {
          await deleteMutation.mutateAsync(confirmState.id);
          setConfirmState(null);
        }}
      />
    </Stack>
  );
};

export default ParentsPage;
